import React, { useState, useEffect } from 'react';
import { ShoppingCart, Wifi, Home, Info, Mail, ChevronDown, Menu, X, Plus, Minus, Trash2, MapPin, DollarSign } from 'lucide-react';

const MOCK_PRODUCTS = [
  {
    id: 1,
    name: 'SmartDoor Pro',
    category: 'Door Sensors',
    price: 79.99,
    image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=400&h=400&fit=crop',
    description: 'Advanced door sensor with real-time alerts and battery life up to 2 years',
    features: ['Instant notifications', 'Battery life: 2 years', 'Easy installation', 'App controlled'],
    inStock: true,
    rating: 4.8
  },
  {
    id: 2,
    name: 'WeatherSense 365',
    category: 'Weather Sensors',
    price: 129.99,
    image: 'https://images.unsplash.com/photo-1592078615290-033ee584e267?w=400&h=400&fit=crop',
    description: 'Complete weather monitoring station with wind, rain, temperature, and humidity sensors',
    features: ['Wind speed tracking', 'Rain detection', 'UV index monitoring', 'Wireless connectivity'],
    inStock: true,
    rating: 4.9
  },
  {
    id: 3,
    name: 'ThermoSmart Elite',
    category: 'Temperature Sensors',
    price: 49.99,
    image: 'https://images.unsplash.com/photo-1545259741-2ea3ebf61fa3?w=400&h=400&fit=crop',
    description: 'Precision temperature and humidity sensor for every room in your home',
    features: ['±0.3°C accuracy', 'Humidity monitoring', 'Historical data', 'Multi-room support'],
    inStock: true,
    rating: 4.7
  },
  {
    id: 4,
    name: 'MotionGuard 2.0',
    category: 'Motion Sensors',
    price: 59.99,
    image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=400&h=400&fit=crop',
    description: 'Smart PIR motion sensor with pet immunity and customizable zones',
    features: ['Pet friendly', 'Adjustable sensitivity', 'Wide angle detection', 'Night vision'],
    inStock: true,
    rating: 4.6
  },
  {
    id: 5,
    name: 'SmartWindow Guardian',
    category: 'Door Sensors',
    price: 69.99,
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=400&fit=crop',
    description: 'Window and door contact sensor with tamper detection',
    features: ['Tamper alerts', 'Compact design', '3-year battery', 'Weather resistant'],
    inStock: true,
    rating: 4.5
  },
  {
    id: 6,
    name: 'AirQuality Pro',
    category: 'Air Quality Sensors',
    price: 159.99,
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=400&fit=crop',
    description: 'Monitor CO2, VOCs, particulate matter, and air quality index in real-time',
    features: ['CO2 monitoring', 'VOC detection', 'PM2.5/PM10 tracking', 'Air quality alerts'],
    inStock: false,
    rating: 4.9
  },
  {
    id: 7,
    name: 'FloodSafe Alert',
    category: 'Water Sensors',
    price: 39.99,
    image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=400&fit=crop',
    description: 'Water leak detector with audio alarm and smartphone notifications',
    features: ['Instant leak alerts', 'Audio alarm', 'Probe cable included', 'Basement ready'],
    inStock: true,
    rating: 4.8
  },
  {
    id: 8,
    name: 'LightSense Ambient',
    category: 'Light Sensors',
    price: 44.99,
    image: 'https://images.unsplash.com/photo-1513506003901-1e6a229e2d15?w=400&h=400&fit=crop',
    description: 'Ambient light sensor for automated lighting and energy savings',
    features: ['Auto brightness', 'Energy efficient', 'Scene automation', 'Daylight harvesting'],
    inStock: true,
    rating: 4.4
  }
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 149.50 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.35 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.53 },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83.12 }
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 
  'Japan', 'India', 'Spain', 'Italy', 'Netherlands', 'Sweden'
];

const IoTStore = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [cart, setCart] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currency, setCurrency] = useState(CURRENCIES[0]);
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [showCart, setShowCart] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const categories = ['All', ...new Set(MOCK_PRODUCTS.map(p => p.category))];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside dropdown containers
      if (!event.target.closest('.dropdown-container')) {
        setShowCurrencyDropdown(false);
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredProducts = MOCK_PRODUCTS.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => 
      item.id === productId 
        ? { ...item, quantity: Math.max(0, item.quantity + delta) }
        : item
    ).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  // Contact form handlers
  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateContactForm = () => {
    const errors = {};
    
    // Name validation
    if (!contactForm.name.trim()) {
      errors.name = 'Name is required';
    } else if (contactForm.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    if (!contactForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Subject validation
    if (!contactForm.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (contactForm.subject.trim().length < 3) {
      errors.subject = 'Subject must be at least 3 characters';
    }
    
    // Message validation
    if (!contactForm.message.trim()) {
      errors.message = 'Message is required';
    } else if (contactForm.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    return errors;
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateContactForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Send email using backend API
      const response = await fetch('http://localhost:5000/api/contact/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name.trim(),
          email: contactForm.email.trim(),
          subject: contactForm.subject.trim(),
          message: contactForm.message.trim(),
          to: 'info@synclones.com'
        })
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setContactForm({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setFormErrors({});
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus(null);
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const convertPrice = (price) => {
    return (price * currency.rate).toFixed(2);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * currency.rate * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const Header = () => (
    <header className="header">
      
      <nav className="main-nav">
        <div className="container">
          <div className="nav-content">
            <div className="logo" onClick={() => setCurrentPage('home')}>
              <img src="https://synclones.com/site/img/short-logo.png" alt="Synclones" className="logo-img" />
              <span className="logo-text">S<span className="logo-accent">ynclones</span></span>
            </div>
            
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
            
            <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              <a className={currentPage === 'home' ? 'active' : ''} onClick={() => { setCurrentPage('home'); setMobileMenuOpen(false); }}>
                <Home size={18} /> Home
              </a>
              <a className={currentPage === 'devices' ? 'active' : ''} onClick={() => { setCurrentPage('devices'); setMobileMenuOpen(false); }}>
                <Wifi size={18} /> Devices
              </a>
              <a className={currentPage === 'about' ? 'active' : ''} onClick={() => { setCurrentPage('about'); setMobileMenuOpen(false); }}>
                <Info size={18} /> About
              </a>
              <a className={currentPage === 'contact' ? 'active' : ''} onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }}>
                <Mail size={18} /> Contact
              </a>
            </div>
            
            <button className="cart-btn" onClick={() => setShowCart(true)}>
              <ShoppingCart />
              {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
            </button>
          </div>
        </div>
      </nav>
    </header>
  );

  const HomePage = () => (
    <div className="page home-page">
      <section className="hero">
        <div className="hero-bg">
          <img src="https://synclones.com/site/img/Synclones.png" alt="Synclones" className="hero-image" />
        </div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-line small-title">Automate Anywhere with</span>
              <span className="title-line accent">Synclones IoT Solutions</span>
            </h1>
            <p className="hero-subtitle">
              Experience the future of home automation with our cutting-edge IoT sensors and devices. 
              Monitor, control, and optimize your space from anywhere in the world.
            </p>
            <button className="cta-btn" onClick={() => setCurrentPage('devices')}>
              Explore Our Devices
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Choose Synclones IoT?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Enterprise Security</h3>
              <p>Enhanced security encryption protects your data and ensures complete privacy</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Analytics</h3>
              <p>Instant insights and notifications delivered directly to your devices</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔋</div>
              <h3>Extended Battery Life</h3>
              <p>Smart power management ensures devices last up to 1 years</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🌐</div>
              <h3>Global Connectivity</h3>
              <p>Seamless integration across all your devices and platforms</p>
            </div>
          </div>
        </div>
      </section>

      <section className="showcase">
        <div className="container">
          <h2 className="section-title">Smart Living, Simplified</h2>
          <div className="showcase-grid">
            <div className="showcase-item">
              <img src="https://synclones.com/site/img/home1.png" alt="Smart Home" />
              <h3>Intelligent Automation</h3>
              <p>Transform your home into a smart ecosystem with our IoT solutions</p>
            </div>
            <div className="showcase-item">
              <img src="https://synclones.com/site/img/home3.png" alt="Analytics" />
              <h3>Data-Driven Insights</h3>
              <p>Make informed decisions with comprehensive analytics and reporting</p>
            </div>
            <div className="showcase-item">
              <img src="https://synclones.com/site/img/home2.png" alt="Analytics" />
              <h3>Energy Efficiency</h3>
              <p>Make informed decisions with comprehensive analytics and reporting</p>
            </div>
          </div>
        </div>
      </section>

      <section className="categories-preview">
        <div className="container">
          <h2 className="section-title">Browse By Category</h2>
          <div className="category-cards">
            {categories.filter(c => c !== 'All').slice(0, 4).map(cat => (
              <div key={cat} className="category-card" onClick={() => { setCurrentPage('devices'); setSelectedCategory(cat); }}>
                <div className="category-icon-large">
                  {cat === 'Door Sensors' && '🚪'}
                  {cat === 'Weather Sensors' && '🌤️'}
                  {cat === 'Temperature Sensors' && '🌡️'}
                  {cat === 'Motion Sensors' && '👁️'}
                </div>
                <h3>{cat}</h3>
                <p>{MOCK_PRODUCTS.filter(p => p.category === cat).length} devices available</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  const DevicesPage = () => (
    <div className="page devices-page">
      <div className="container">
        <div className="devices-header">
          <h1 className="page-title">Our Smart Devices</h1>
          <p className="page-subtitle">Discover our range of intelligent IoT sensors designed for modern living</p>
          <div className="devices-controls">
            <input 
              type="text" 
              className="search-input"
              placeholder="Search devices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <select 
              className="category-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="product-image-container">
                <img src={product.image} alt={product.name} className="product-image" />
                {!product.inStock && <div className="out-of-stock-badge">Out of Stock</div>}
                <div className="product-overlay">
                  <button 
                    className="quick-view-btn"
                    onClick={() => setSelectedProduct(product)}
                  >
                    View Details
                  </button>
                </div>
              </div>
              <div className="product-info">
                <span className="product-category">{product.category}</span>
                <h3 className="product-name">{product.name}</h3>
                <div className="product-rating">
                  {'⭐'.repeat(Math.floor(product.rating))} {product.rating}
                </div>
                <p className="product-description">{product.description}</p>
                <div className="product-footer">
                  <span className="product-price">
                    {currency.symbol}{convertPrice(product.price)}
                  </span>
                  <button 
                    className="add-to-cart-btn"
                    onClick={() => addToCart(product)}
                    disabled={!product.inStock}
                  >
                    {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ProductModal = () => {
    if (!selectedProduct) return null;

    return (
      <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={() => setSelectedProduct(null)}>
            <X />
          </button>
          <div className="modal-body">
            <div className="modal-image">
              <img src={selectedProduct.image} alt={selectedProduct.name} />
            </div>
            <div className="modal-details">
              <span className="modal-category">{selectedProduct.category}</span>
              <h2 className="modal-title">{selectedProduct.name}</h2>
              <div className="modal-rating">
                {'⭐'.repeat(Math.floor(selectedProduct.rating))} {selectedProduct.rating} / 5.0
              </div>
              <p className="modal-description">{selectedProduct.description}</p>
              <div className="modal-features">
                <h4>Key Features:</h4>
                <ul>
                  {selectedProduct.features.map((feature, idx) => (
                    <li key={idx}>✓ {feature}</li>
                  ))}
                </ul>
              </div>
              <div className="modal-price-section">
                <span className="modal-price">{currency.symbol}{convertPrice(selectedProduct.price)}</span>
                {selectedProduct.inStock ? (
                  <button 
                    className="modal-add-btn"
                    onClick={() => {
                      addToCart(selectedProduct);
                      setSelectedProduct(null);
                    }}
                  >
                    Add to Cart
                  </button>
                ) : (
                  <button className="modal-add-btn" disabled>Out of Stock</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CartSidebar = () => (
    <div className={`cart-sidebar ${showCart ? 'open' : ''}`}>
      <div className="cart-overlay" onClick={() => setShowCart(false)}></div>
      <div className="cart-content">
        <div className="cart-header">
          <h2>Shopping Cart ({cartItemCount})</h2>
          <button onClick={() => setShowCart(false)}>
            <X />
          </button>
        </div>
        
        <div className="cart-items">
          {cart.length === 0 ? (
            <div className="empty-cart">
              <ShoppingCart size={64} />
              <p>Your cart is empty</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="cart-item">
                <img src={item.image} alt={item.name} />
                <div className="cart-item-details">
                  <h4>{item.name}</h4>
                  <p className="cart-item-price">{currency.symbol}{convertPrice(item.price)}</p>
                  <div className="quantity-controls">
                    <button onClick={() => updateQuantity(item.id, -1)}>
                      <Minus size={16} />
                    </button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                <button className="remove-btn" onClick={() => removeFromCart(item.id)}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))
          )}
        </div>
        
        {cart.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total">
              <span>Total:</span>
              <span className="total-amount">{currency.symbol}{cartTotal.toFixed(2)}</span>
            </div>
            <button className="checkout-btn">
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className="page about-page">
      <div className="container">
        <h1 className="page-title">About Synclones</h1>
        <div className="about-content">
          <div className="about-section">
            <h2>Our Mission</h2>
            <p>
              At Synclones, we specialize in Internet of Things (IoT) based automation solutions. 
              Our mission is to transform the way businesses and homes operate by providing intelligent, 
              seamless, and scalable IoT solutions that drive efficiency, productivity, and connectivity.
            </p>
          </div>
          
          <div className="about-section">
            <h2>Who We Are</h2>
            <p>
              We are a team of passionate technologists, engineers, and innovators dedicated to leveraging 
              the power of IoT to solve complex challenges. With deep understanding of the digital landscape, 
              we deliver tailored automation solutions that meet unique needs across various industries.
            </p>
          </div>
          
          <div className="about-section">
            <h2>Our Vision</h2>
            <p>
              Our vision is to become a global leader in IoT-based automation, empowering businesses and 
              individuals to achieve unprecedented levels of efficiency and innovation. We aim to create 
              a future where interconnected devices and smart automation systems work harmoniously to 
              enhance quality of life.
            </p>
          </div>
          
          <div className="about-section">
            <h2>Our Values</h2>
            <div className="values-grid">
              <div className="value-card">
                <h3>🎯 Innovation</h3>
                <p>Constantly pushing boundaries of IoT technology and automation</p>
              </div>
              <div className="value-card">
                <h3>🛡️ Security</h3>
                <p>Your privacy and data security are our top priorities</p>
              </div>
              <div className="value-card">
                <h3>💚 Sustainability</h3>
                <p>Building energy-efficient products that minimize environmental impact</p>
              </div>
              <div className="value-card">
                <h3>🤝 Excellence</h3>
                <p>Delivering world-class solutions with exceptional support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ContactPage = () => (
    <div className="page contact-page">
      <div className="container">
        <h1 className="page-title">Contact Us</h1>
        <div className="contact-content">
          <div className="contact-info">
            
            <div className="contact-details">
				<div>
            <h2>Get in Touch</h2>
            <p>We're here to help with all your IoT automation needs!</p>
			</div>
              <div className="contact-item">
                <Mail />
                <div>
                  <h4>Email</h4>
                  <p>info@synclones.com</p>
                </div>
              </div>
              <div className="contact-item">
                <div>📞</div>
                <div>
                  <h4>Phone</h4>
                  <p>+1-800-IOT-SMART</p>
                  <p className="hours">Mon-Fri: 9:00 AM - 6:00 PM</p>
                </div>
              </div>
              <div className="contact-item">
                <MapPin />
                <div>
                  <h4>Address</h4>
                  <p>Company Address</p>
                  <p>City, State, Zip Code</p>
                </div>
              </div>
            </div>
          </div>
          
          <form className="contact-form" onSubmit={handleContactFormSubmit}>
            <h3>Send us a Message</h3>
            
            {submitStatus === 'success' && (
              <div className="alert alert-success">
                ✅ Thank you for your message! We'll get back to you soon.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="alert alert-error">
                ❌ Something went wrong. Please try again or contact us directly at info@synclones.com
              </div>
            )}
            
            <div className="form-group">
              <label>Name *</label>
              <input 
                type="text" 
                name="name"
                placeholder="Your name" 
                value={contactForm.name}
                onChange={handleContactFormChange}
                className={formErrors.name ? 'input-error' : ''}
              />
              {formErrors.name && <span className="error-text">{formErrors.name}</span>}
            </div>
            
            <div className="form-group">
              <label>Email *</label>
              <input 
                type="email" 
                name="email"
                placeholder="your@email.com" 
                value={contactForm.email}
                onChange={handleContactFormChange}
                className={formErrors.email ? 'input-error' : ''}
              />
              {formErrors.email && <span className="error-text">{formErrors.email}</span>}
            </div>
            
            <div className="form-group">
              <label>Subject *</label>
              <input 
                type="text" 
                name="subject"
                placeholder="How can we help?" 
                value={contactForm.subject}
                onChange={handleContactFormChange}
                className={formErrors.subject ? 'input-error' : ''}
              />
              {formErrors.subject && <span className="error-text">{formErrors.subject}</span>}
            </div>
            
            <div className="form-group">
              <label>Message *</label>
              <textarea 
                rows="5" 
                name="message"
                placeholder="Your message..."
                value={contactForm.message}
                onChange={handleContactFormChange}
                className={formErrors.message ? 'input-error' : ''}
              ></textarea>
              {formErrors.message && <span className="error-text">{formErrors.message}</span>}
            </div>
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app">
      <Header />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'devices' && <DevicesPage />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'contact' && <ContactPage />}
      {selectedProduct && <ProductModal />}
      <CartSidebar />
      
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img src="https://synclones.com/site/img/short-logo.png" alt="Synclones" className="footer-logo" />
              <p>Automate anywhere with Synclones IoT solutions</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <a onClick={() => setCurrentPage('home')}>Home</a>
              <a onClick={() => setCurrentPage('devices')}>Devices</a>
              <a onClick={() => setCurrentPage('about')}>About</a>
              <a onClick={() => setCurrentPage('contact')}>Contact</a>
            </div>
            <div className="footer-section">
              <h4>Support</h4>
              <a href="#">FAQ</a>
              <a href="#">Shipping</a>
              <a href="#">Returns</a>
              <a href="#">Warranty</a>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">Security</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Synclones. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8f0f8 100%);
          color: #2c3e50;
          overflow-x: hidden;
        }

        .app {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        /* Header Styles - Light Theme with Blue/Green */
        .header {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 0 2px 20px rgba(41, 128, 185, 0.1);
          border-bottom: 1px solid rgba(52, 152, 219, 0.15);
        }

        .top-bar {
          background: linear-gradient(90deg, #3498db 0%, #2ecc71 100%);
          padding: 0.6rem 0;
          font-size: 0.85rem;
        }

        .top-bar-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          color: white;
        }

        .location-currency {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .dropdown-container {
          position: relative;
        }

        .dropdown-trigger {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: white;
          padding: 0.4rem 0.8rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }

        .dropdown-trigger:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .dropdown-menu {
          position: absolute;
          top: calc(100% + 0.5rem);
          left: 0;
          background: white;
          border: 1px solid #e0e6ed;
          border-radius: 12px;
          min-width: 200px;
          max-height: 300px;
          overflow-y: auto;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          animation: dropdownSlide 0.2s ease;
          z-index: 1000;
        }

        @keyframes dropdownSlide {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .dropdown-item {
          padding: 0.75rem 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #2c3e50;
          border-bottom: 1px solid #f0f3f7;
        }

        .dropdown-item:last-child {
          border-bottom: none;
        }

        .dropdown-item:hover {
          background: linear-gradient(90deg, rgba(52, 152, 219, 0.1) 0%, rgba(46, 204, 113, 0.1) 100%);
          color: #3498db;
        }

        .contact-info {
          display: flex;
          gap: 2rem;
          color: #f60;;
        }

        .main-nav {
          padding: 1rem 0;
        }

        .nav-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .logo:hover {
          transform: scale(1.05);
        }

        .logo-img {
          height: 40px;
          width: auto;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ed7a4e;
          font-family: 'Poppins', sans-serif;
        }

        .logo-accent {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .mobile-menu-btn {
          display: none;
          background: transparent;
          border: none;
          color: #3498db;
          cursor: pointer;
          padding: 0.5rem;
        }

        .nav-links {
          display: flex;
          gap: 2rem;
          align-items: center;
        }

        .nav-links a {
          color: #5a6c7d;
          text-decoration: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 10px;
          transition: all 0.3s ease;
          font-weight: 500;
        }

        .nav-links a:hover,
        .nav-links a.active {
          color: #3498db;
          background: rgba(52, 152, 219, 0.08);
        }

        .cart-btn {
          position: relative;
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          border: none;
          color: white;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
        }

        .cart-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(52, 152, 219, 0.4);
        }

        .cart-badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background: #e74c3c;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
          animation: bounce 0.5s ease;
        }

        @keyframes bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        /* Hero Section - Light Theme */
        .hero {
          position: relative;
          padding: 6rem 0 5rem;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
        }

        .hero-bg {
          position: absolute;
          top: 50%;
          right: 5%;
          transform: translateY(-50%);
          opacity: 0.15;
          z-index: 0;
        }

        .hero-image {
          max-width: 500px;
          height: auto;
        }

        .hero-content {
          position: relative;
          max-width: 700px;
          z-index: 1;
        }

        .hero-title {
          font-size: 4rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          font-family: 'Poppins', sans-serif;
        }

        .title-line {
          display: block;
          animation: slideInUp 0.8s ease backwards;
          color: #2c3e50;
        }
		
		.small-title{
          font-size: 3rem;
		}

        .title-line:nth-child(2) {
          animation-delay: 0.2s;
        }

        .title-line.accent {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: #5a6c7d;
          margin-bottom: 2.5rem;
          animation: fadeIn 1s ease 0.4s backwards;
          line-height: 1.8;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .cta-btn {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 1.25rem 3rem;
          font-size: 1.1rem;
          font-weight: 700;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
          box-shadow: 0 10px 30px rgba(52, 152, 219, 0.3);
          animation: fadeIn 1s ease 0.6s backwards;
        }

        .cta-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(52, 152, 219, 0.4);
        }

        .btn-arrow {
          transition: transform 0.3s ease;
        }

        .cta-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        /* Features Section */
        .features {
          padding: 5rem 0;
          background: white;
        }

        .section-title {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 3rem;
          color: #2c3e50;
          font-family: 'Poppins', sans-serif;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
          padding: 2.5rem;
          border-radius: 20px;
          border: 2px solid rgba(52, 152, 219, 0.1);
          transition: all 0.3s ease;
          text-align: center;
        }

        .feature-card:hover {
          transform: translateY(-10px);
          border-color: #3498db;
          box-shadow: 0 20px 40px rgba(52, 152, 219, 0.15);
          background: white;
        }

        .feature-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .feature-card h3 {
          font-size: 1.4rem;
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .feature-card p {
          color: #5a6c7d;
          line-height: 1.6;
        }

        /* Showcase Section */
        .showcase {
          padding: 5rem 0;
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.03) 0%, rgba(46, 204, 113, 0.03) 100%);
        }

        .showcase-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 3rem;
          margin-top: 3rem;
        }

        .showcase-item {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .showcase-item:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 50px rgba(52, 152, 219, 0.15);
        }

        .showcase-item img {
          width: 100%;
		  height: 520px;
          object-fit: contain;
          border-radius: 12px;
          margin-bottom: 1.5rem;
        }

        .showcase-item h3 {
          font-size: 1.5rem;
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .showcase-item p {
          color: #5a6c7d;
          line-height: 1.6;
        }

        /* Categories Preview */
        .categories-preview {
          padding: 5rem 0;
          background: white;
        }

        .category-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .category-card {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.08) 0%, rgba(46, 204, 113, 0.08) 100%);
          padding: 3rem 2rem;
          border-radius: 20px;
          border: 2px solid rgba(52, 152, 219, 0.15);
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
        }

        .category-card:hover {
          transform: translateY(-10px) scale(1.02);
          border-color: #3498db;
          box-shadow: 0 20px 50px rgba(52, 152, 219, 0.2);
          background: white;
        }

        .category-icon-large {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .category-card h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
          color: #2c3e50;
        }

        .category-card p {
          color: #5a6c7d;
        }

        /* Devices Page */
        .devices-page {
          padding: 4rem 0;
          flex: 1;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8f0f8 100%);
        }

        .page-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #2c3e50;
          font-family: 'Poppins', sans-serif;
        }

        .page-subtitle {
          font-size: 1.2rem;
          color: #5a6c7d;
          margin-bottom: 2rem;
        }

        .devices-controls {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
          flex-wrap: wrap;
        }

        .search-input,
        .category-select {
          padding: 0.875rem 1.25rem;
          background: white;
          border: 2px solid rgba(52, 152, 219, 0.2);
          border-radius: 12px;
          color: #2c3e50;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .search-input {
          flex: 1;
          min-width: 250px;
        }

        .category-select {
          min-width: 200px;
        }

        .search-input:focus,
        .category-select:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 2.5rem;
          margin-top: 3rem;
        }

        .product-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          border: 2px solid rgba(52, 152, 219, 0.1);
          transition: all 0.3s ease;
          animation: fadeInScale 0.5s ease backwards;
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .product-card:hover {
          transform: translateY(-10px);
          border-color: #3498db;
          box-shadow: 0 20px 50px rgba(52, 152, 219, 0.2);
        }

        .product-image-container {
          position: relative;
          height: 280px;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .product-card:hover .product-image {
          transform: scale(1.05);
        }

        .out-of-stock-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(231, 76, 60, 0.95);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .product-overlay {
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .product-card:hover .product-overlay {
          opacity: 1;
        }

        .quick-view-btn {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          border-radius: 50px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          transform: translateY(10px);
        }

        .product-card:hover .quick-view-btn {
          transform: translateY(0);
        }

        .quick-view-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 30px rgba(52, 152, 219, 0.3);
        }

        .product-info {
          padding: 1.5rem;
        }

        .product-category {
          color: #3498db;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .product-name {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0.5rem 0;
          color: #2c3e50;
        }

        .product-rating {
          color: #f39c12;
          font-size: 0.9rem;
          margin-bottom: 0.75rem;
        }

        .product-description {
          color: #5a6c7d;
          line-height: 1.6;
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }

        .product-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 2px solid rgba(52, 152, 219, 0.1);
        }

        .product-price {
          font-size: 1.75rem;
          font-weight: 700;
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .add-to-cart-btn {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-to-cart-btn:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 5px 15px rgba(52, 152, 219, 0.3);
        }

        .add-to-cart-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #95a5a6;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(44, 62, 80, 0.85);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 2rem;
          animation: fadeIn 0.3s ease;
        }

        .modal-content {
          background: white;
          border-radius: 24px;
          max-width: 1000px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          border: 2px solid rgba(52, 152, 219, 0.2);
          animation: modalSlide 0.3s ease;
        }

        @keyframes modalSlide {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .modal-close {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          background: rgba(231, 76, 60, 0.1);
          border: none;
          color: #e74c3c;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
        }

        .modal-close:hover {
          background: rgba(231, 76, 60, 0.2);
          transform: rotate(90deg);
        }

        .modal-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          padding: 3rem;
        }

        .modal-image {
          border-radius: 16px;
          overflow: hidden;
          height: 500px;
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
        }

        .modal-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .modal-details {
          display: flex;
          flex-direction: column;
        }

        .modal-category {
          color: #3498db;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1.5px;
        }

        .modal-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 1rem 0;
          color: #2c3e50;
        }

        .modal-rating {
          color: #f39c12;
          font-size: 1.1rem;
          margin-bottom: 1.5rem;
        }

        .modal-description {
          color: #5a6c7d;
          line-height: 1.8;
          margin-bottom: 2rem;
          font-size: 1.05rem;
        }

        .modal-features {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
          padding: 1.5rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          border: 2px solid rgba(52, 152, 219, 0.1);
        }

        .modal-features h4 {
          color: #2c3e50;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .modal-features ul {
          list-style: none;
        }

        .modal-features li {
          color: #5a6c7d;
          margin-bottom: 0.75rem;
          line-height: 1.6;
        }

        .modal-price-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 2rem;
          border-top: 2px solid rgba(52, 152, 219, 0.15);
          margin-top: auto;
        }

        .modal-price {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .modal-add-btn {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 1rem 2.5rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(52, 152, 219, 0.3);
        }

        .modal-add-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(52, 152, 219, 0.4);
        }

        .modal-add-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #95a5a6;
        }

        /* Cart Sidebar */
        .cart-sidebar {
          position: fixed;
          top: 0;
          right: -100%;
          width: 100%;
          max-width: 450px;
          height: 100vh;
          background: white;
          transition: right 0.3s ease;
          z-index: 2000;
          box-shadow: -10px 0 40px rgba(0, 0, 0, 0.1);
        }

        .cart-sidebar.open {
          right: 0;
        }

        .cart-overlay {
          position: fixed;
          inset: 0;
          background: rgba(44, 62, 80, 0.5);
          backdrop-filter: blur(5px);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          z-index: 1999;
        }

        .cart-sidebar.open ~ .cart-overlay,
        .cart-sidebar.open .cart-overlay {
          opacity: 1;
          pointer-events: all;
        }

        .cart-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 2000;
        }

        .cart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 2rem;
          border-bottom: 2px solid rgba(52, 152, 219, 0.15);
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
        }

        .cart-header h2 {
          color: #2c3e50;
          font-size: 1.5rem;
        }

        .cart-header button {
          background: transparent;
          border: none;
          color: #5a6c7d;
          cursor: pointer;
          padding: 0.5rem;
          transition: color 0.3s ease;
        }

        .cart-header button:hover {
          color: #3498db;
        }

        .cart-items {
          flex: 1;
          overflow-y: auto;
          padding: 2rem;
        }

        .empty-cart {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #95a5a6;
          gap: 1rem;
        }

        .empty-cart svg {
          opacity: 0.3;
        }

        .cart-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.03) 0%, rgba(46, 204, 113, 0.03) 100%);
          border-radius: 12px;
          border: 2px solid rgba(52, 152, 219, 0.1);
        }

        .cart-item img {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 8px;
        }

        .cart-item-details {
          flex: 1;
        }

        .cart-item-details h4 {
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .cart-item-price {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
          margin-bottom: 0.75rem;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .quantity-controls button {
          background: rgba(52, 152, 219, 0.1);
          border: 1px solid rgba(52, 152, 219, 0.2);
          color: #3498db;
          width: 30px;
          height: 30px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .quantity-controls button:hover {
          background: rgba(52, 152, 219, 0.2);
          border-color: #3498db;
        }

        .quantity-controls span {
          color: #2c3e50;
          font-weight: 600;
          min-width: 30px;
          text-align: center;
        }

        .remove-btn {
          background: transparent;
          border: none;
          color: #e74c3c;
          cursor: pointer;
          padding: 0.5rem;
          transition: all 0.3s ease;
        }

        .remove-btn:hover {
          color: #c0392b;
          transform: scale(1.1);
        }

        .cart-footer {
          padding: 2rem;
          border-top: 2px solid rgba(52, 152, 219, 0.15);
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
        }

        .cart-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          font-size: 1.25rem;
        }

        .cart-total span:first-child {
          color: #5a6c7d;
        }

        .total-amount {
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 700;
          font-size: 1.75rem;
        }

        .checkout-btn {
          width: 100%;
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 1.25rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .checkout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(52, 152, 219, 0.3);
        }

        /* About & Contact Pages */
        .about-page,
        .contact-page {
          padding: 4rem 0;
          flex: 1;
          background: white;
        }

        .about-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .about-section {
          margin-bottom: 4rem;
        }

        .about-section h2 {
          font-size: 2rem;
          color: #2c3e50;
          margin-bottom: 1.5rem;
        }

        .about-section p {
          color: #5a6c7d;
          line-height: 1.8;
          font-size: 1.1rem;
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
        }

        .value-card {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
          padding: 2rem;
          border-radius: 16px;
          border: 2px solid rgba(52, 152, 219, 0.1);
          transition: all 0.3s ease;
        }

        .value-card:hover {
          transform: translateY(-5px);
          border-color: #3498db;
          box-shadow: 0 15px 40px rgba(52, 152, 219, 0.15);
        }

        .value-card h3 {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          color: #2c3e50;
        }

        .value-card p {
          color: #5a6c7d;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .contact-content {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 4rem;
          margin-top: 3rem;
        }

        .contact-info h2 {
          font-size: 2rem;
          color: #2c3e50;
          margin-bottom: 1rem;
        }

        .contact-info > p {
          color: #5a6c7d;
          margin-bottom: 3rem;
          font-size: 1.1rem;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .contact-item {
          display: flex;
          gap: 1.5rem;
          align-items: flex-start;
        }

        .contact-item > div:first-child,
        .contact-item > svg {
          color: #3498db;
          font-size: 1.5rem;
          min-width: 24px;
        }

        .contact-item h4 {
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .contact-item p {
          color: #5a6c7d;
          line-height: 1.6;
        }

        .contact-item .hours {
          font-size: 0.9rem;
          color: #3498db;
          margin-top: 0.25rem;
        }

        .contact-form {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
          padding: 3rem;
          border-radius: 20px;
          border: 2px solid rgba(52, 152, 219, 0.1);
        }

        .contact-form h3 {
          color: #2c3e50;
          font-size: 1.75rem;
          margin-bottom: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          color: #2c3e50;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 1rem;
          background: white;
          border: 2px solid rgba(52, 152, 219, 0.2);
          border-radius: 12px;
          color: #2c3e50;
          font-size: 1rem;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .input-error {
          border-color: #e74c3c !important;
        }

        .input-error:focus {
          box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1) !important;
        }

        .error-text {
          color: #e74c3c;
          font-size: 0.85rem;
          margin-top: 0.5rem;
          display: block;
        }

        .alert {
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          font-weight: 500;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .alert-success {
          background: rgba(46, 204, 113, 0.1);
          border: 2px solid #2ecc71;
          color: #27ae60;
        }

        .alert-error {
          background: rgba(231, 76, 60, 0.1);
          border: 2px solid #e74c3c;
          color: #c0392b;
        }

        .submit-btn {
          width: 100%;
          background: linear-gradient(135deg, #3498db 0%, #2ecc71 100%);
          color: white;
          border: none;
          padding: 1.25rem;
          border-radius: 50px;
          font-weight: 700;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(52, 152, 219, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Footer */
        .footer {
          background: linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(46, 204, 113, 0.05) 100%);
          margin-top: auto;
          padding: 4rem 0 2rem;
          border-top: 2px solid rgba(52, 152, 219, 0.15);
        }

        .footer-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 3rem;
          margin-bottom: 3rem;
        }

        .footer-logo {
          height: 40px;
          width: auto;
          margin-bottom: 1rem;
        }

        .footer-section h3,
        .footer-section h4 {
          color: #2c3e50;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .footer-section p {
          color: #5a6c7d;
          line-height: 1.6;
        }

        .footer-section a {
          display: block;
          color: #5a6c7d;
          text-decoration: none;
          margin-bottom: 0.75rem;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .footer-section a:hover {
          color: #3498db;
        }

        .footer-bottom {
          text-align: center;
          padding-top: 2rem;
          border-top: 2px solid rgba(52, 152, 219, 0.1);
          color: #5a6c7d;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: block;
          }

          .nav-links {
            position: fixed;
            top: 120px;
            left: 0;
            right: 0;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(20px);
            flex-direction: column;
            padding: 2rem;
            gap: 1rem;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            border-bottom: 2px solid rgba(52, 152, 219, 0.2);
          }

          .nav-links.mobile-open {
            transform: translateX(0);
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .hero-bg {
            opacity: 0.05;
            right: -10%;
          }

          .hero-image {
            max-width: 300px;
          }

          .features-grid,
          .category-cards,
          .products-grid,
          .showcase-grid {
            grid-template-columns: 1fr;
          }

          .modal-body {
            grid-template-columns: 1fr;
          }

          .modal-image {
            height: 300px;
          }

          .contact-content {
            grid-template-columns: 1fr;
          }

          .top-bar-content {
            flex-direction: column;
            align-items: flex-start;
          }

          .contact-info {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default IoTStore;
